import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { Calendar, Ship, Boxes, User, RefreshCw } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import { Button } from "@/components/ui/button";
import { useTerminal } from "@/context/TerminalContext";

const HOUR_PX = 60; // pixels per hour for gantt

export default function Timeline() {
  const { currentTerminal, currentTerminalId } = useTerminal();
  const [data, setData] = useState(null);
  const [terminals, setTerminals] = useState([]);
  const [vessels, setVessels] = useState([]);
  const [assets, setAssets] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [conditions, setConditions] = useState(null);
  const [loading, setLoading] = useState(false);
  const terminalId = currentTerminalId;

  const refresh = async (forceConditions = false) => {
    setLoading(true);
    try {
      const [tl, vs, ts, ta, pp] = await Promise.all([
        api.list("timeline", terminalId ? { terminal_id: terminalId } : {}),
        api.list("vessels"),
        api.list("terminals"),
        api.list("terminal-assets"),
        api.list("personnel"),
      ]);
      setData(tl); setVessels(vs); setTerminals(ts); setAssets(ta); setPersonnel(pp);
      const tid = terminalId || ts[0]?.id;
      if (tid) {
        const c = await api.conditions(tid, forceConditions);
        setConditions(c);
      }
    } finally { setLoading(false); }
  };
  // Refresh is intentionally tied to persistent terminal context.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refresh(); }, [terminalId]);

  if (!data) return <div className="text-slate-500 font-mono text-xs">Loading timeline…</div>;

  // Compute Gantt window: earliest ETA to latest ETD
  const pcs = data.items || [];
  const starts = pcs.map((it) => new Date(it.port_call.eta).getTime()).filter(Boolean);
  const ends = pcs.map((it) => new Date(it.port_call.etd).getTime()).filter(Boolean);
  const windowStart = starts.length ? Math.min(...starts) : Date.now();
  const windowEnd = ends.length ? Math.max(...ends) : windowStart + (24 * 3600 * 1000);
  const windowMs = Math.max(windowEnd - windowStart, 3600 * 1000);
  const windowHours = Math.ceil(windowMs / 3600000);
  const totalWidth = Math.max(windowHours * HOUR_PX, 800);

  const xFor = (iso) => {
    const t = new Date(iso).getTime();
    return ((t - windowStart) / windowMs) * totalWidth;
  };

  const vName = (id) => vessels.find((v) => v.id === id)?.name || id?.slice(0, 8);
  const resName = (a) => (a.type === "asset" ? assets.find((x) => x.id === a.resource_id)?.name : personnel.find((x) => x.id === a.resource_id)?.name) || a.resource_id?.slice(0, 8);

  // Tide chart data
  const tideHourly = conditions?.marine?.hourly;
  const tideChart = tideHourly
    ? (tideHourly.time || []).slice(0, 24).map((t, i) => ({
        time: t.slice(11, 16),
        sea_level: tideHourly.sea_level_m?.[i] ?? tideHourly.sea_level_height_msl?.[i] ?? null,
        wave: tideHourly.wave_height?.[i] ?? null,
      }))
    : [];
  const windHourly = conditions?.weather?.hourly;
  const windChart = windHourly
    ? (windHourly.time || []).slice(0, 24).map((t, i) => ({
        time: t.slice(11, 16),
        wind: windHourly.wind_speed_10m?.[i] ?? null,
        gust: windHourly.wind_gusts_10m?.[i] ?? null,
      }))
    : [];

  // Hour ticks
  const ticks = [];
  for (let h = 0; h <= windowHours; h += 2) ticks.push(h);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-slate-500">Timeline</div>
          <h1 className="font-heading text-3xl md:text-4xl font-black tracking-tighter text-slate-50">Operations Timeline</h1>
          <p className="text-sm text-slate-500 mt-1">Conditions and reservations for {currentTerminal?.name || "the current terminal"}.</p>
          <div className="font-mono text-[11px] text-slate-500 mt-1">
            {new Date(windowStart).toISOString().slice(0, 16).replace("T", " ")} → {new Date(windowEnd).toISOString().slice(0, 16).replace("T", " ")} UTC
          </div>
          <Link to="/help#timeline-conditions" className="text-xs text-sky-300 hover:text-sky-200">Timeline and conditions guide</Link>
        </div>
        <Button data-testid="btn-refresh-timeline" size="sm" onClick={() => refresh(true)} disabled={loading} className="bg-[#162032] hover:bg-[#1E2B41] border border-[#1E293B] rounded-none text-slate-200">
          <RefreshCw size={14} className={loading ? "mr-1.5 animate-spin" : "mr-1.5"} /> Refresh
        </Button>
      </div>

      {/* Tide + wind charts side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="panel p-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-2">24h Tide Forecast · {terminals.find((t) => t.id === terminalId)?.name || "—"} · {conditions?.marine?.vertical_datum || "datum unknown"}</div>
          {tideChart.length > 0 ? (
            <ResponsiveContainer width="100%" height={180}>
              <LineChart data={tideChart}>
                <CartesianGrid stroke="#D9E4E8" strokeDasharray="3 3" />
                <XAxis dataKey="time" stroke="#64748B" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} />
                <YAxis stroke="#64748B" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} domain={["auto", "auto"]} />
                <Tooltip contentStyle={{ background: "#FFFFFF", border: "1px solid #D9E4E8", borderRadius: 8, fontFamily: "IBM Plex Mono", fontSize: 11 }} />
                <ReferenceLine y={0} stroke="#475569" strokeDasharray="2 2" />
                <Line type="monotone" dataKey="sea_level" stroke="#38BDF8" strokeWidth={2} dot={false} name="Sea level (m)" />
                <Line type="monotone" dataKey="wave" stroke="#F59E0B" strokeWidth={1.5} dot={false} name="Wave height (m)" strokeDasharray="3 3" />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-xs text-slate-500 font-mono">No marine forecast available.</div>
          )}
        </div>
        <div className="panel p-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-2">24h Wind Forecast (kn)</div>
          {windChart.length > 0 ? (
            <ResponsiveContainer width="100%" height={180}>
              <LineChart data={windChart}>
                <CartesianGrid stroke="#D9E4E8" strokeDasharray="3 3" />
                <XAxis dataKey="time" stroke="#64748B" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} />
                <YAxis stroke="#64748B" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} />
                <Tooltip contentStyle={{ background: "#FFFFFF", border: "1px solid #D9E4E8", borderRadius: 8, fontFamily: "IBM Plex Mono", fontSize: 11 }} />
                <ReferenceLine y={30} stroke="#F59E0B" strokeDasharray="3 3" label={{ value: "30kn warn", fill: "#F59E0B", fontSize: 9 }} />
                <ReferenceLine y={40} stroke="#EF4444" strokeDasharray="3 3" label={{ value: "40kn crit", fill: "#EF4444", fontSize: 9 }} />
                <Line type="monotone" dataKey="wind" stroke="#10B981" strokeWidth={2} dot={false} name="Wind (kn)" />
                <Line type="monotone" dataKey="gust" stroke="#94A3B8" strokeWidth={1.5} dot={false} name="Gust (kn)" strokeDasharray="3 3" />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-xs text-slate-500 font-mono">No wind forecast.</div>
          )}
        </div>
      </div>

      {/* Gantt */}
      <div className="panel p-4 overflow-x-auto" data-testid="gantt-chart">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-3">Port Call &amp; Asset Reservations (Gantt)</div>
        {pcs.length === 0 && <div className="empty-state compact">No port calls scheduled for this terminal.</div>}
        <div style={{ width: totalWidth + 220, position: "relative" }}>
          {/* Hour header */}
          <div className="flex items-center" style={{ marginLeft: 220, position: "relative" }}>
            <div className="relative h-6" style={{ width: totalWidth }}>
              {ticks.map((h) => (
                <div key={h} className="absolute text-[10px] text-slate-500 font-mono" style={{ left: h * HOUR_PX, top: 0 }}>
                  +{h}h
                </div>
              ))}
            </div>
          </div>
          {/* Rows */}
          {pcs.map((item) => {
            const pc = item.port_call;
            const x0 = xFor(pc.eta);
            const x1 = xFor(pc.etd);
            const width = Math.max(x1 - x0, 8);
            const sev = pc.status === "approved" ? "#10B981" : pc.status === "submitted" ? "#38BDF8" : pc.status === "rejected" ? "#EF4444" : "#F59E0B";
            return (
              <div key={pc.id} className="border-t border-[#1E293B] py-2" data-testid={`gantt-row-${pc.id}`}>
                <div className="flex items-center">
                  <Link to={`/port-calls/${pc.id}`} className="w-[220px] flex-shrink-0 pr-3">
                    <div className="text-sm text-slate-100 flex items-center gap-1.5 hover:text-sky-300">
                      <Ship size={12} className="text-sky-400" /> {vName(pc.vessel_id)}
                    </div>
                    <div className="font-mono text-[10px] text-slate-500">{pc.status}</div>
                  </Link>
                  <div className="relative" style={{ height: 24, width: totalWidth }}>
                    {ticks.map((h) => (
                      <div key={h} className="absolute h-full" style={{ left: h * HOUR_PX, top: 0, borderLeft: "1px solid #1E293B", opacity: 0.5 }} />
                    ))}
                    <div
                      className="absolute h-5 top-0.5 border"
                      style={{
                        left: x0,
                        width,
                        background: sev + "30",
                        borderColor: sev,
                      }}
                    >
                      <span className="absolute left-1 top-0.5 text-[10px] font-mono text-slate-100 whitespace-nowrap">
                        {pc.eta?.slice(11, 16)} → {pc.etd?.slice(11, 16)}
                      </span>
                    </div>
                  </div>
                </div>
                {/* Assignment sub-rows */}
                {item.assignments.map((a) => {
                  const ax0 = xFor(a.start || pc.eta);
                  const ax1 = xFor(a.end || pc.etd);
                  const aw = Math.max(ax1 - ax0, 6);
                  return (
                    <div key={a.id} className="flex items-center mt-1">
                      <div className="w-[220px] flex-shrink-0 pl-6 pr-3 font-mono text-[11px] text-slate-400 truncate flex items-center gap-1">
                        {a.type === "asset" ? <Boxes size={10} className="text-amber-400" /> : <User size={10} className="text-sky-400" />}
                        {resName(a)}
                      </div>
                      <div className="relative" style={{ height: 14, width: totalWidth }}>
                        <div className="absolute h-3 top-0.5" style={{ left: ax0, width: aw, background: a.type === "asset" ? "rgba(245,158,11,0.5)" : "rgba(56,189,248,0.5)", border: "1px solid", borderColor: a.type === "asset" ? "#F59E0B" : "#38BDF8" }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
